/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FiTrendingUp, FiDollarSign, FiShoppingCart,
  FiTag, FiCheckCircle, FiClock,
} from "react-icons/fi";
import PageContainer from "../../components/PageContainer";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "../../../utils/formatNumber";
import {
  ResponsiveContainer, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

/* ─── tiny live-clock hook ─────────────────────────────────────────────── */
function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/* ─── stat card ─────────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, title, value, accent, isDark, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      style={{
        background: isDark
          ? "linear-gradient(135deg,#1e293b 0%,#0f172a 100%)"
          : "#ffffff",
        border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
        borderRadius: 20,
        padding: "22px 20px",
        boxShadow: isDark
          ? "0 4px 24px rgba(0,0,0,0.35)"
          : "0 2px 16px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* soft glow blob */}
      <div
        style={{
          position: "absolute", top: -20, right: -20,
          width: 90, height: 90, borderRadius: "50%",
          background: accent,
          opacity: 0.12, filter: "blur(20px)",
          pointerEvents: "none",
        }}
      />

      {/* icon */}
      <div
        style={{
          width: 44, height: 44, borderRadius: 14,
          background: `linear-gradient(135deg,${accent}22,${accent}44)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: `1px solid ${accent}33`,
        }}
      >
        <Icon size={20} color={accent} />
      </div>

      <div>
        <p style={{
          fontSize: 12, fontWeight: 600,
          color: isDark ? "#94a3b8" : "#64748b",
          marginBottom: 6, lineHeight: 1.4,
        }}>
          {title}
        </p>
        <p style={{
          fontSize: 22, fontWeight: 900,
          color: accent, lineHeight: 1,
          letterSpacing: "-0.5px",
        }}>
          {value}
        </p>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Analytics() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const now = useLiveClock();

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq").replace(/\/+$/, "");
  const rootUrl = baseUrl.replace(/\/api$/i, "");
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalOrdersAmount: 0, totalOrdersWithCoupon: 0, totalOrdersCount: 0, completedOrdersAmount: 0 });
  const [salesData, setSalesData] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    if (!perms || perms.viewAnalytics === undefined) return;
    if (perms?.viewAnalytics !== true) { setLoading(false); return; }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const headers = { "Accept": "application/json", "Authorization": token ? `Bearer ${token}` : "" };
      const pub = { "Accept": "application/json" };

      const safeJson = async (res, fb) => {
        if (!res.ok) return fb;
        const ct = res.headers.get("content-type") || "";
        return ct.includes("application/json") ? res.json() : fb;
      };

      /* ── summary cards ── */
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${rootUrl}/api/ActiveOrders?tenantId=${tenantId}`, { headers: pub }),
        fetch(`${rootUrl}/api/ActiveOrders/TotalOrdersWithCoupon?tenantId=${tenantId}`, { headers: pub }),
        fetch(`${rootUrl}/api/ActiveOrders/TotalOrdersCount?tenantId=${tenantId}`, { headers: pub }),
        fetch(`${rootUrl}/api/ActiveOrders/CompletedOrdersAmount?tenantId=${tenantId}`, { headers: pub }),
      ]);
      const [d1, d2, d3, d4] = await Promise.all([
        safeJson(r1, { totalOrdersAmount: 0 }),
        safeJson(r2, { totalOrdersWithCoupon: 0 }),
        safeJson(r3, { totalOrdersCount: 0 }),
        safeJson(r4, { completedOrdersAmount: 0 }),
      ]);
      setStats({
        totalOrdersAmount: d1.totalOrdersAmount || 0,
        totalOrdersWithCoupon: d2.totalOrdersWithCoupon || 0,
        totalOrdersCount: d3.totalOrdersCount || 0,
        completedOrdersAmount: d4.completedOrdersAmount || 0,
      });

      /* ── chart ── */
      const ordersRes = await fetch(`${baseUrl}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, { headers });
      const rawOrders = await safeJson(ordersRes, []);
      if (Array.isArray(rawOrders)) {
        const seen = new Set();
        const unique = rawOrders.filter(o => {
          if (!o.orderId || seen.has(o.orderId)) return false;
          seen.add(o.orderId); return true;
        });
        const map = new Map();
        unique.forEach(order => {
          const raw = (order.orderDate || order.createdAt || "").toString();
          if (!raw) return;
          const fixed = raw.replace(/\s+/g, " ").replace(/(\d)(AM|PM)/i, "$1 $2");
          const d = new Date(Date.parse(fixed));
          if (isNaN(d)) return;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          const name = d.toLocaleString(isArabic ? "ar-EG" : "en-US", { month: "short", year: "2-digit" });
          if (!map.has(key)) map.set(key, { key, period: name, completed: 0, active: 0, cancelled: 0, count: 0 });
          const e = map.get(key);
          e.count += 1;
          const amount = Array.isArray(order.details) && order.details.length > 0
            ? order.details.reduce((s, i) => s + parseFloat(i.lineTotal || 0), 0)
            : Number(order.netAmount || order.totalAmount || 0);
          const status = (order.status || "").toLowerCase();
          const isCompleted = order.statusId === 5 || status.includes("complet") || status.includes("deliver");
          const isCancelled = [6, 7, 8, 9].includes(order.statusId);
          if (isCompleted) e.completed += amount;
          else if (isCancelled) e.cancelled += (amount || 1); // show count even if amount=0
          else e.active += amount;
        });
        setSalesData(Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key)).slice(-6));
      }
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, baseUrl, rootUrl, isArabic, perms?.viewAnalytics]); // eslint-disable-line

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (!perms.viewAnalytics) return <NoAccess />;

  /* ── clock strings ── */
  const timeStr = now.toLocaleTimeString(isArabic ? "ar-EG" : "en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const dateStr = now.toLocaleDateString(isArabic ? "ar-EG" : "en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const CARDS = [
    {
      icon: FiCheckCircle,
      title: isArabic ? "المبيعات المكتملة" : "Completed Sales",
      value: `${formatNumber(Math.round(stats.completedOrdersAmount))} ${isArabic ? "د.ع" : "IQD"}`,
      accent: colors.green || "#22c55e",
    },
    {
      icon: FiShoppingCart,
      title: isArabic ? "إجمالي الطلبات" : "Total Orders",
      value: formatNumber(stats.totalOrdersCount),
      accent: colors.mainColor,
    },
    {
      icon: FiTag,
      title: isArabic ? "طلبات بكوبون" : "Orders with Coupon",
      value: formatNumber(stats.totalOrdersWithCoupon),
      accent: colors.gold || "#f59e0b",
    },
    {
      icon: FiTrendingUp,
      title: isArabic ? "قيمة الطلبات النشطة" : "Active Orders Value",
      value: `${formatNumber(Math.round(stats.totalOrdersAmount))} ${isArabic ? "د.ع" : "IQD"}`,
      accent: colors.blue || "#6366f1",
    },
  ];

  return (
    <PageContainer title={isArabic ? "التحليلات" : "Analytics"}>

      {/* ── Hero header ────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          borderRadius: 24,
          padding: "28px 28px",
          marginBottom: 28,
          background: isDark
            ? `linear-gradient(135deg, #1e1b4b 0%, #0f172a 60%, #1e293b 100%)`
            : `linear-gradient(135deg, ${colors.mainColor}18 0%, ${colors.mainColor}06 100%)`,
          border: `1px solid ${isDark ? "#312e81" : `${colors.mainColor}22`}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* bg blur blobs */}
        <div style={{
          position: "absolute", top: -40, left: -40,
          width: 160, height: 160, borderRadius: "50%",
          background: colors.mainColor, opacity: 0.08, filter: "blur(40px)",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -30, right: 60,
          width: 120, height: 120, borderRadius: "50%",
          background: colors.gold || "#f59e0b", opacity: 0.07, filter: "blur(35px)",
          pointerEvents: "none",
        }} />

        {/* left: title */}
        <div style={{ position: "relative" }}>
          <h1 style={{
            fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase",
            color: colors.mainColor, marginBottom: 4, opacity: 0.9
          }}>
            {isArabic ? "لوحة البيانات" : "DASHBOARD"}
          </h1>

          <p style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", marginTop: 4, fontWeight: 500 }}>
            {isArabic ? "نظرة شاملة على أداء متجرك" : "A comprehensive overview of your store performance"}
          </p>
        </div>

        {/* right: live clock */}
        <div style={{
          display: "flex", flexDirection: "column", alignItems: isArabic ? "flex-start" : "flex-end",
          gap: 2, position: "relative",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiClock size={14} color={colors.mainColor} />
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: isDark ? "#94a3b8" : "#64748b", letterSpacing: 1
            }}>
              {isArabic ? "الوقت الحالي" : "LIVE TIME"}
            </span>
          </div>
          <p style={{
            fontSize: 28, fontWeight: 900, fontVariantNumeric: "tabular-nums",
            color: isDark ? "#f1f5f9" : "#1e293b", letterSpacing: "-1px", lineHeight: 1,
          }}>
            {timeStr}
          </p>
          <p style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 500 }}>
            {dateStr}
          </p>
        </div>
      </motion.div>

      {/* ── Stat Cards ─────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", marginBottom: 24 }}>
          <div style={{
            display: "inline-block", width: 44, height: 44, borderRadius: "50%",
            border: `3px solid ${colors.mainColor}33`,
            borderTop: `3px solid ${colors.mainColor}`,
            animation: "spin 0.8s linear infinite",
          }} />
          <p style={{ marginTop: 14, fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
            {isArabic ? "جاري تحميل البيانات..." : "Loading analytics..."}
          </p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 16,
          marginBottom: 28,
        }}>
          {CARDS.map((c, i) => (
            <StatCard key={i} {...c} isDark={isDark} delay={i * 0.08} />
          ))}
        </div>
      )}

      {/* ── Bar Chart ─────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div style={{
          borderRadius: 24,
          border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
          padding: "24px 24px 16px",
          background: isDark
            ? "linear-gradient(135deg,#1e293b 0%,#0f172a 100%)"
            : "#ffffff",
          boxShadow: isDark ? "0 4px 32px rgba(0,0,0,0.35)" : "0 2px 20px rgba(0,0,0,0.06)",
        }}>

          {/* chart header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 20, flexWrap: "wrap", gap: 12
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 4, height: 36, borderRadius: 4,
                background: `linear-gradient(to bottom,${colors.mainColor},${colors.mainColor}44)`,
              }} />
              <div>
                <h3 style={{
                  fontSize: 16, fontWeight: 900,
                  color: isDark ? "#f1f5f9" : "#1e293b", margin: 0
                }}>
                  {isArabic ? "المبيعات الشهرية" : "Monthly Sales"}
                </h3>
                <p style={{
                  fontSize: 12, color: isDark ? "#94a3b8" : "#64748b",
                  margin: "3px 0 0", fontWeight: 500
                }}>
                  {isArabic ? "آخر 6 أشهر — مكتملة مقابل نشطة" : "Last 6 months — Completed vs Active"}
                </p>
              </div>
            </div>

            {/* legend pills */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { label: isArabic ? "مكتملة" : "Completed", color: colors.green || "#22c55e" },
                { label: isArabic ? "نشطة" : "Active", color: colors.mainColor },
                { label: isArabic ? "ملغاة" : "Cancelled", color: "#ef4444" },
              ].map((l, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: isDark ? "#0f172a" : "#f8fafc",
                  border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  borderRadius: 20, padding: "5px 12px",
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: l.color }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>
                    {l.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* chart body */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <div style={{
                display: "inline-block", width: 36, height: 36, borderRadius: "50%",
                border: `3px solid ${colors.mainColor}33`,
                borderTop: `3px solid ${colors.mainColor}`,
                animation: "spin 0.8s linear infinite",
              }} />
            </div>
          ) : salesData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "56px 0" }}>
              <FiDollarSign size={48} style={{ opacity: 0.15, color: isDark ? "#f1f5f9" : "#1e293b" }} />
              <p style={{
                marginTop: 12, fontSize: 14, fontWeight: 700,
                color: isDark ? "#94a3b8" : "#64748b", opacity: 0.5
              }}>
                {isArabic ? "لا توجد بيانات للعرض" : "No data available"}
              </p>
            </div>
          ) : (
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barGap={8} barCategoryGap="30%">
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.green || "#22c55e"} stopOpacity={1} />
                      <stop offset="100%" stopColor={colors.green || "#22c55e"} stopOpacity={0.35} />
                    </linearGradient>
                    <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.mainColor} stopOpacity={1} />
                      <stop offset="100%" stopColor={colors.mainColor} stopOpacity={0.35} />
                    </linearGradient>
                    <linearGradient id="gx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.35} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" vertical={false}
                    stroke={isDark ? "#1e293b" : "#f1f5f9"} />

                  <XAxis dataKey="period" axisLine={false} tickLine={false}
                    tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12, fontWeight: 700 }} dy={10} />

                  <YAxis axisLine={false} tickLine={false}
                    tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 11, fontWeight: 600 }}
                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                    width={52} />

                  <Tooltip
                    cursor={{ fill: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", radius: 8 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const entry = salesData.find(d => d.period === label);
                      return (
                        <div style={{
                          background: isDark ? "#1e293b" : "#fff",
                          border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                          borderRadius: 16, padding: "14px 18px",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                          minWidth: 190,
                        }}>
                          <p style={{
                            fontSize: 11, fontWeight: 800, letterSpacing: 2,
                            textTransform: "uppercase", color: isDark ? "#94a3b8" : "#64748b", marginBottom: 4
                          }}>
                            {label}
                          </p>
                          {entry && (
                            <p style={{
                              fontSize: 11, color: isDark ? "#64748b" : "#94a3b8",
                              fontWeight: 600, marginBottom: 10
                            }}>
                              {isArabic ? `${entry.count} طلب` : `${entry.count} orders`}
                            </p>
                          )}
                          {payload.map((p, i) => {
                            const isCancelledBar = p.dataKey === "cancelled";
                            return (
                              <div key={i} style={{
                                display: "flex", alignItems: "center",
                                justifyContent: "space-between", gap: 24, marginBottom: 6
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
                                  <span style={{
                                    fontSize: 13, fontWeight: 700,
                                    color: isDark ? "#cbd5e1" : "#475569"
                                  }}>{p.name}</span>
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 900, color: p.color }}>
                                  {isCancelledBar
                                    ? formatNumber(Math.round(p.value))
                                    : formatNumber(Math.round(p.value))}
                                  {!isCancelledBar && (
                                    <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 4 }}>
                                      {isArabic ? "د.ع" : "IQD"}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  />

                  <Legend content={() => null} /> {/* using custom pills above */}

                  <Bar dataKey="completed" name={isArabic ? "مكتملة" : "Completed"}
                    fill="url(#gc)" radius={[10, 10, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="active" name={isArabic ? "نشطة" : "Active"}
                    fill="url(#ga)" radius={[10, 10, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="cancelled" name={isArabic ? "ملغاة" : "Cancelled"}
                    fill="url(#gx)" radius={[10, 10, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </motion.div>

    </PageContainer>
  );
}
